import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { useAccountHook } from "../../../../../../hooks/useAccountHook";
import { Account } from "../../../../../../models/account";
import { Error } from "../../../../../../models/error";
import { Filters, Searches, Sorts } from "../../../../../../models/shipeezi.table";
import AccountsService from "../../../../../../services/account.service";
import { checkError } from "../../../../../../util/checkError";
import { Status } from "../../../../../../util/enums";
import { handlesGrid } from "../../../../../../util/handlesGrid";

export const useActions = () => {
  const { currentUser } = useAppState();

  const { setAccount } = useAccountHook();

  const { t: translate } = useTranslation();

  const [accountsData, setAccountsData] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState<number | undefined>(undefined);

  const [filters, setFilters] = useState<Filters>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [responseError, setResponseError] = useState<Error>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();
  const [total, setTotal] = useState<number>(0);

  const { handleCurrentPage, handleFilter, handlePageSize, handleSearch, handleSort } = handlesGrid(
    { setFilters, setSorting, setPage, setPageSize, setSearches }
  );

  const getAccounts = () =>
    useQuery(
      ["accounts.list", page, pageSize, searches, filters, sorting],
      () =>
        AccountsService.getAll({
          page,
          pageSize,
          companyId: currentUser?.companyId,
          sortBy:
            sorting && sorting?.length > 0
              ? JSON.stringify(sorting)
              : JSON.stringify([["updatedAt", "DESC"]]),
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          setAccountsData(response.data);
        },
      }
    );

  const getAccountById = () =>
    useQuery(["account.get.by.id", accountId], () => AccountsService.getById(accountId || 0), {
      enabled: !!accountId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setAccount(response.data);
        }
      },
    });

  const getAccountsCount = () =>
    useQuery(
      ["accounts.count", filters, searches],
      () =>
        AccountsService.count({
          companyId: currentUser?.companyId,
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...filters,
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          setTotal(response?.data || 0);
        },
      }
    );

  const getActiveAccountsCount = () =>
    useQuery(
      ["accounts.active.count"],
      () =>
        AccountsService.count({
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
        }),
      { enabled: !!currentUser?.id }
    );

  const useCreateAccount = (callbackSuccess?: () => void) =>
    useMutation(["account.create"], (account: Account) => AccountsService.create(account), {
      onSuccess: () => {
        setResponseError(undefined);
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (callbackSuccess) callbackSuccess();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setResponseError(error?.response?.data);
        }
      },
    });

  const useUpdateAccount = (callbackSuccess?: () => void) =>
    useMutation(
      ["account.update"],
      ({ id, account }: { id: number; account: Account }) => AccountsService.update(id, account),
      {
        onSuccess: () => {
          setResponseError(undefined);
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          if (callbackSuccess) callbackSuccess();
        },
        onError: (error: AxiosError<Error>) => {
          if (error && error.response) {
            checkError(error, translate("anErrorOccurredTryAgain"));
            setResponseError(error?.response?.data);
          }
        },
      }
    );

  return {
    accountsData,
    filters,
    getAccountById,
    getAccounts,
    getAccountsCount,
    getActiveAccountsCount,

    handleCurrentPage,
    handleFilter,
    handlePageSize,
    handleSearch,
    handleSort,

    page,
    pageSize,
    responseError,
    setAccountId,
    accountId,
    total,

    useCreateAccount,
    useUpdateAccount,
  };
};
